import React from "react";
import { Drawer, Modal } from "antd";
import Icon from "@mdi/react";
import { mdiClose, mdiEmail, mdiPhone, mdiWhatsapp } from "@mdi/js";
import { emailLink, whatsappLink } from "../../../../utils/helpers";
import useBreakpoint from "../../../../utils/useBreakpoint";
import { Agent } from "./bookingUtils";

interface Contact { whatsapp?: string | string[]; phone?: string | string[]; email?: string | string[] }

// Phones and emails can arrive as one string or a list; every number/address gets its own row.
const toList = (value?: string | string[]): string[] =>
  (Array.isArray(value) ? value : String(value || "").split(/[,;\n]/)).map((item) => String(item).trim()).filter(Boolean);

function ContactCard({ title, contact, mobile }: { title: string; contact: Contact; mobile: boolean }) {
  const groups = [
    { label: "WhatsApp", values: toList(contact.whatsapp), href: (value: string) => whatsappLink(value), icon: mdiWhatsapp, tone: "wa" },
    { label: "Phone", values: toList(contact.phone), href: (value: string) => `tel:${value}`, icon: mdiPhone, tone: "phone" },
    { label: "Email", values: toList(contact.email), href: (value: string) => emailLink(value), icon: mdiEmail, tone: "mail" },
  ].filter((group) => group.values.length);
  if (!groups.length) return null;
  return (
    <div className="gt-contact-card">
      <h4>{title}</h4>
      {groups.map((group) => (
        <div className="gt-contact-card__group" key={group.label}>
          {group.values.map((value, index) => (
            <div className="gt-contact-card__row" key={value}>
              <div>{index === 0 && <span>{group.label}</span>}<strong>{value}</strong></div>
              {mobile && (
                <a href={group.href(value)} target={group.tone === "wa" ? "_blank" : undefined} rel="noreferrer" className={`is-${group.tone}`} aria-label={`${group.label} ${value}`}>
                  <Icon path={group.icon} size="20px" />
                </a>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

const nameOf = (agent: Pick<Agent, "agency_full_name" | "name">) => agent.agency_full_name || agent.name;

// "Agent Info" (Figma 11190:274155 desktop modal, 10308:317816 mobile sheet): agency contact, plus the
// Trip Advisor's when the partner came from a Trip Advisor's referral link or sticky binding.
export default function PartnerDetails({ agent, onClose }: { agent: Agent | null; onClose: () => void }) {
  const isMobile = useBreakpoint();
  const sub = agent?.subagent;
  const address = agent ? [agent.address, agent.city, agent.state].filter(Boolean).join(", ") : "";

  const body = agent && (
    <div className="gt-agent-info">
      <div className="gt-agent-info__head">
        {agent.image ? <img src={agent.image} alt="" /> : <span>{nameOf(agent).slice(0, 1)}</span>}
        <h3>{nameOf(agent)}</h3>
      </div>
      {address && <div className="gt-agent-info__address"><small>{nameOf(agent)}</small><p>{address}</p></div>}
      <div className="gt-agent-info__cards">
        <ContactCard title="Agency Contact" mobile={isMobile} contact={{ whatsapp: agent.whatsapp, phone: agent.phone, email: agent.email }} />
        {sub && <ContactCard title="Trip Advisor Contact" mobile={isMobile} contact={{ whatsapp: sub.whatsapp, phone: sub.phone, email: sub.email }} />}
      </div>
    </div>
  );

  const header = (
    <div className="gt-dialog__head"><h3>Agent Info</h3><button type="button" onClick={onClose} aria-label="Close"><Icon path={mdiClose} size="22px" /></button></div>
  );

  if (isMobile) {
    return (
      <Drawer open={!!agent} onClose={onClose} placement="bottom" height="auto" closable={false} title={header} rootClassName="gt-sheet-drawer" destroyOnClose>
        {body}
      </Drawer>
    );
  }
  return (
    <Modal open={!!agent} onCancel={onClose} footer={null} centered closable={false} width={800} wrapClassName="gt-dialog" destroyOnClose>
      {header}
      {body}
    </Modal>
  );
}
